import React from 'react';
import {
    ActivityIndicator,
    Platform,
    Pressable,
    StyleSheet,
    Text,
    TextInput,
    View,
} from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import {
    ProjectFeedFeedbackFilter,
} from '../../lib/projectPaperFeedback';
import { ProjectFeedSortOption } from '../../lib/hooks/useProjectFeed';
import ProjectFeedFilterBlock from './ProjectFeedFilterBlock';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

interface ProjectFeedListControlsProps {
    shownCount: number;
    totalCount: number;
    searchQuery: string;
    feedbackFilter: ProjectFeedFeedbackFilter;
    sortOption: ProjectFeedSortOption;
    isSearchMode?: boolean;
    hasMoreSearchResults?: boolean;
    isSearchPending?: boolean;
    showSearchAllPapersHint?: boolean;
    onSearchQueryChange: (value: string) => void;
    onFeedbackFilterChange: (filter: ProjectFeedFeedbackFilter) => void;
    onSortOptionChange: (sort: ProjectFeedSortOption) => void;
}

export default function ProjectFeedListControls({
    shownCount,
    totalCount,
    searchQuery,
    feedbackFilter,
    sortOption,
    isSearchMode = false,
    hasMoreSearchResults = false,
    isSearchPending = false,
    showSearchAllPapersHint = false,
    onSearchQueryChange,
    onFeedbackFilterChange,
    onSortOptionChange,
}: ProjectFeedListControlsProps) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();
    const countLabel = isSearchMode
        ? hasMoreSearchResults
            ? i18n._({
                ...msg({ id: 'feed.search.resultCountMore', message: '{count}+ results' }),
                values: { count: shownCount },
            })
            : i18n._({
                ...msg({ id: 'feed.search.resultCount', message: '{count, plural, one {# result} other {# results}}' }),
                values: { count: shownCount },
            })
        : shownCount < totalCount
            ? i18n._({
                ...msg({ id: 'feed.search.paperCountShown', message: '{shown} of {total} papers' }),
                values: { shown: shownCount, total: totalCount },
            })
            : i18n._({
                ...msg({ id: 'feed.search.paperCount', message: '{count, plural, one {# paper} other {# papers}}' }),
                values: { count: shownCount },
            });

    return (
        <View style={styles.shell} testID="project-feed-list-controls">
            <View style={styles.searchShell}>
                <FontAwesome6 name="magnifying-glass" size={12} color={colors.text500} />
                <TextInput
                    style={[styles.searchInput, fontsLoaded && interRegular]}
                    placeholder={i18n._(msg({ id: "feed.projectfeedlistcontrols.searchTitleAuthorsAbstractOrSummary", message: "Search title, authors, abstract, or summary...", comment: "UI placeholder in components/feed/ProjectFeedListControls.tsx" }))}
                    placeholderTextColor={colors.text500}
                    value={searchQuery}
                    onChangeText={onSearchQueryChange}
                />
                {searchQuery ? (
                    <Pressable onPress={() => onSearchQueryChange('')} style={styles.clearButton}>
                        <FontAwesome6 name="xmark" size={12} color={colors.text500} />
                    </Pressable>
                ) : null}
            </View>
            {isSearchPending ? (
                <View style={styles.helperRow} testID="project-feed-search-pending">
                    <ActivityIndicator size="small" color={colors.primaryGreen} />
                    <Text style={[styles.helperText, fontsLoaded && interRegular]}><Trans id="feed.projectfeedlistcontrols.searchingAllPapers" comment="UI copy in components/feed/ProjectFeedListControls.tsx">
                        Searching all papers...
                    </Trans></Text>
                </View>
            ) : showSearchAllPapersHint ? (
                <Text
                    style={[styles.helperText, fontsLoaded && interRegular]}
                    testID="project-feed-search-all-hint"
                ><Trans id="feed.projectfeedlistcontrols.keepTypingToSearchAllPapers" comment="UI copy in components/feed/ProjectFeedListControls.tsx">
                    Keep typing to search all papers
                </Trans></Text>
            ) : null}

            <ProjectFeedFilterBlock
                feedbackFilter={feedbackFilter}
                onFeedbackFilterChange={onFeedbackFilterChange}
                compact={true}
            />

            <View style={styles.footerRow}>
                <Text style={[styles.countText, fontsLoaded && interRegular]}>
                    {countLabel}
                </Text>
                <View style={styles.sortRow}>
                    {isSearchMode ? (
                        <Text
                            style={[styles.sortLabel, fontsLoaded && interRegular]}
                            testID="project-feed-sort-match"
                        ><Trans id="feed.projectfeedlistcontrols.sortedByMatch" comment="UI copy in components/feed/ProjectFeedListControls.tsx">
                            Sorted by match
                        </Trans></Text>
                    ) : (
                        <>
                            <Text style={[styles.sortLabel, fontsLoaded && interRegular]}><Trans id="feed.projectfeedlistcontrols.sortBy" comment="UI copy in components/feed/ProjectFeedListControls.tsx">
                                Sort by
                            </Trans></Text>
                            <Pressable
                                onPress={() => onSortOptionChange('date')}
                                testID="project-feed-sort-date"
                                style={styles.sortPressable}
                            >
                                <Text
                                    style={[
                                        styles.sortLink,
                                        sortOption === 'date' ? styles.sortLinkActive : styles.sortLinkInactive,
                                        fontsLoaded && (sortOption === 'date' ? interSemiBold : interRegular),
                                    ]}
                                ><Trans id="feed.projectfeedlistcontrols.date" comment="UI copy in components/feed/ProjectFeedListControls.tsx">
                                    date
                                </Trans></Text>
                            </Pressable>
                            <Text style={[styles.sortSeparator, fontsLoaded && interRegular]}>|</Text>
                            <Pressable
                                onPress={() => onSortOptionChange('relevance')}
                                testID="project-feed-sort-relevance"
                                style={styles.sortPressable}
                            >
                                <Text
                                    style={[
                                        styles.sortLink,
                                        sortOption === 'relevance'
                                            ? styles.sortLinkActive
                                            : styles.sortLinkInactive,
                                        fontsLoaded && (sortOption === 'relevance' ? interSemiBold : interRegular),
                                    ]}
                                ><Trans id="feed.projectfeedlistcontrols.relevance" comment="UI copy in components/feed/ProjectFeedListControls.tsx">
                                    relevance
                                </Trans></Text>
                            </Pressable>
                        </>
                    )}
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    shell: {
        gap: 12,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 14,
        backgroundColor: colors.card,
        borderBottomWidth: 1,
        borderBottomColor: '#edf1f5',
    },
    countText: {
        fontSize: 12,
        lineHeight: 18,
        color: colors.text500,
    },
    helperText: {
        fontSize: 12,
        lineHeight: 18,
        color: colors.text500,
    },
    helperRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: -4,
    },
    footerRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
    },
    sortRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        rowGap: 4,
        columnGap: 8,
        marginLeft: 'auto',
        flexShrink: 1,
    },
    sortLabel: {
        fontSize: 12,
        lineHeight: 18,
        color: colors.text600,
    },
    sortPressable: {
        paddingVertical: 1,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    sortLink: {
        fontSize: 12,
        lineHeight: 18,
    },
    sortLinkActive: {
        color: colors.text900,
    },
    sortLinkInactive: {
        color: colors.text600,
    },
    sortSeparator: {
        fontSize: 12,
        lineHeight: 18,
        color: colors.text500,
    },
    searchShell: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        minHeight: 42,
        paddingHorizontal: 12,
        borderWidth: 1,
        borderColor: '#d9dde5',
        borderRadius: 12,
        backgroundColor: '#ffffff',
    },
    searchInput: {
        flex: 1,
        fontSize: 14,
        color: colors.text900,
        ...(Platform.OS === 'web' && {
            outlineStyle: 'none',
        } as any),
    },
    clearButton: {
        padding: 4,
    },
});
